import {
  OutcomeParentRanges,
  OutcomeParentRangesByIdCode,
  ParentRangesByIdCode,
  SimulationVariableRange,
} from "../type/simulation-def";

/** 数値の小数桁数。指数表記 (1e-7 など) にも対応する。 */
function decimalsOf(value: number): number {
  const text = String(value);
  if (text.includes("e") || text.includes("E")) {
    const [mantissa, exponent] = text.toLowerCase().split("e");
    const mantissaDecimals = (mantissa.split(".")[1] ?? "").length;
    return Math.max(0, mantissaDecimals - Number(exponent));
  }
  return (text.split(".")[1] ?? "").length;
}

/**
 * 値を min からの step 刻みに丸め、[min, max] に収める。
 * 浮動小数の誤差（0.1 刻みで 4.800000000000001 など）も落とす。
 */
export function snapToStep(
  value: number,
  min: number,
  max: number,
  step: number
): number {
  if (!(step > 0)) return Math.min(Math.max(value, min), max);

  const snapped = min + Math.round((value - min) / step) * step;
  const clamped = Math.min(Math.max(snapped, min), max);

  // step の小数桁に合わせて丸める。
  // String(1e-7) は "1e-7" になり split(".") では桁数が取れないため、
  // 指数表記も見て桁数を求める（BE は step: 0.0001 のような細かい値を返す）。
  return Number(clamped.toFixed(decimalsOf(step)));
}

export class ParentRangesAdapter {
  /**
   * 親変数の一覧とレンジを正規化する。
   *
   * `parents` に載っていても `variable_ranges` に無い変数はスライダーを出せないので除く。
   * 逆に `variable_ranges` にしか無いものは `parents` を正として無視する。
   */
  static toOutputDTO(
    res: OutcomeParentRanges | null | undefined
  ): OutcomeParentRanges {
    const ranges = res?.variable_ranges ?? {};
    const parents = (res?.parents ?? []).filter((name) => !!ranges[name]);

    const variable_ranges: Record<string, SimulationVariableRange> = {};
    parents.forEach((name) => {
      const r = ranges[name];
      variable_ranges[name] = {
        min: Number(r.min),
        max: Number(r.max),
        // step が 0 や未定義だとスライダーが動かないので 1 に寄せる
        step: Number(r.step) > 0 ? Number(r.step) : 1,
      };
    });

    return { outcome: res?.outcome ?? "", parents, variable_ranges };
  }

  /**
   * 識別コード別の実測値を正規化する。
   *
   * レンジは識別コードごとに返ってくるが、どのコードでも同じ値のはずなので
   * 先頭のものを代表として使う（スライダーの min / max / step は 1 組で足りる）。
   * 実測値は `snapToStep` で刻みに乗せる。生の値が step からずれていると
   * そのまま送ったときに `INTERVENTION_VALUES_INVALID` になるため。
   */
  static toByIdCodeDTO(
    res: OutcomeParentRangesByIdCode | null | undefined
  ): ParentRangesByIdCode {
    const data = res?.data ?? {};
    const userIds = (res?.all_user_ids ?? []).filter((id) => !!data[id]);

    const first = userIds.length > 0 ? data[userIds[0]].variable_ranges : {};
    const parents = (res?.parents ?? []).filter((name) => !!first[name]);

    const variable_ranges: Record<string, SimulationVariableRange> = {};
    parents.forEach((name) => {
      const r = first[name];
      variable_ranges[name] = {
        min: Number(r.min),
        max: Number(r.max),
        step: Number(r.step) > 0 ? Number(r.step) : 1,
      };
    });

    const valuesByUserId: Record<string, Record<string, number>> = {};
    userIds.forEach((id) => {
      const ranges = data[id].variable_ranges ?? {};
      const values: Record<string, number> = {};
      parents.forEach((name) => {
        const raw = Number(ranges[name]?.value);
        if (!Number.isFinite(raw)) return;
        const { min, max, step } = variable_ranges[name];
        values[name] = snapToStep(raw, min, max, step);
      });
      valuesByUserId[id] = values;
    });

    return {
      outcome: res?.outcome ?? "",
      parents,
      variable_ranges,
      userIds,
      valuesByUserId,
    };
  }

  /**
   * スライダーの初期値。レンジの中央を既定とするが、**必ず step の刻みに乗せる**。
   *
   * 単純な中央値だと step=1 の 0〜9 で 4.5 のような値になり、
   * 整数しか受け付けない列で `INTERVENTION_VALUES_INVALID` になる。
   * スライダーを一度も動かさなかった変数がそのまま送られるため、ここで揃えておく。
   */
  static initialValues(def: OutcomeParentRanges): Record<string, number> {
    const values: Record<string, number> = {};
    def.parents.forEach((name) => {
      const { min, max, step } = def.variable_ranges[name];
      values[name] = snapToStep(min + (max - min) / 2, min, max, step);
    });
    return values;
  }
}
